// src/pages/Inventory/STNApproval.jsx
// ✅ Converted from: stnapproval.aspx + stnapproval.aspx.cs
// ✅ Uses Node routes: /api/inventory/stn-approval/*
// ✅ NO rights logic (excluded as instructed)
// ✅ All business logic preserved:
//    - FillGrid: usp_GetSTNRequestForApproval → Tables[0] header + Tables[1] items
//    - Outward check: usp_IsSTNRequestOutwarded → disable all controls if true
//    - Status pre-select from sessionStorage (Approved/DisApproved/Cancelled)
//    - Cancel → Close disabled; Close → Cancel/Approve/Disapprove disabled
//    - Submit: usp_UpdateSTNRequestStatus
//    - On success → SMS window open + navigate back to listing
// ✅ Pagination added (client-side)
// ✅ Theme-aware: follows exact same ThemeContext + getCurrentSkin pattern as listing page

import { useEffect, useContext, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import LayoutWrapper from "../../components/LayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ArrowLeft, Send, ChevronLeft, ChevronRight, RefreshCcw,
  CheckCircle, XCircle, MinusCircle, Lock
} from "lucide-react";

const BASE_URL = "http://nissi-india.com";

// ─────────────────────── helpers ───────────────────────
function fmtDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d)) return v;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

// ─────────────────────── Pagination ───────────────────────
function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;
  const pages = [];
  let start = Math.max(1, page - 3);
  let end   = Math.min(totalPages, start + 6);
  if (end - start < 6) start = Math.max(1, end - 6);
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div className="flex items-center justify-center gap-1 mt-3 flex-wrap">
      <button
        onClick={() => onPage(page - 1)}
        disabled={page === 1}
        className="p-1.5 rounded-lg disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
      >
        <ChevronLeft size={15} />
      </button>

      {start > 1 && (
        <>
          <button onClick={() => onPage(1)}
            className="px-2.5 py-1 rounded-lg text-xs hover:bg-gray-200 dark:hover:bg-gray-700">1</button>
          <span className="text-xs px-1">…</span>
        </>
      )}

      {pages.map(p => (
        <button key={p} onClick={() => onPage(p)}
          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition
            ${p === page ? "bg-blue-600 text-white" : "hover:bg-gray-200 dark:hover:bg-gray-700"}`}>
          {p}
        </button>
      ))}

      {end < totalPages && (
        <>
          <span className="text-xs px-1">…</span>
          <button onClick={() => onPage(totalPages)}
            className="px-2.5 py-1 rounded-lg text-xs hover:bg-gray-200 dark:hover:bg-gray-700">
            {totalPages}
          </button>
        </>
      )}

      <button
        onClick={() => onPage(page + 1)}
        disabled={page === totalPages}
        className="p-1.5 rounded-lg disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
      >
        <ChevronRight size={15} />
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════
export default function STNApproval() {
  const navigate    = useNavigate();
  const { theme }   = useContext(ThemeContext);
  const isDark      = theme === "dark";
  const currentSkin = getCurrentSkin();
  const { baseColor, baseShade } = currentSkin;
  const primaryBtnClass   = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // ── session values ──
  const stnRequisitionId = sessionStorage.getItem("STNRequisitionId") || "";
  const sessionStatus    = sessionStorage.getItem("STNReqStatus")     || "";

  // ── style helpers (same pattern as listing page) ──
  const inputBase = `w-full px-3 py-2 rounded-xl border outline-none transition text-sm ` +
    (isDark
      ? "bg-gray-800/60 border-white/10 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-400");

  const labelBase = `text-xs font-semibold uppercase tracking-wide ${isDark ? "text-gray-300" : "text-gray-600"}`;

  const cardBase  = `rounded-2xl border shadow-lg overflow-hidden ` +
    (isDark ? "border-white/10 bg-gray-800/40 backdrop-blur-md" : "border-gray-200 bg-white");

  const thBase = `px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-white ${primaryBtnClass}`;
  const tdBase = `px-3 py-2 text-xs ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const readonlyField = `w-full px-3 py-2 rounded-xl border text-sm font-medium ` +
    (isDark
      ? "bg-gray-700/50 border-white/10 text-white"
      : "bg-gray-50 border-gray-200 text-gray-800");

  // ─────────────────── Header State ───────────────────
  const [stnNo,           setStnNo]           = useState("");
  const [date,            setDate]            = useState("");
  const [requestedStore,  setRequestedStore]  = useState("");
  const [department,      setDepartment]      = useState("");
  const [employee,        setEmployee]        = useState("");
  const [indentRef,       setIndentRef]       = useState("");
  const [remarks,         setRemarks]         = useState("");

  // ─────────────────── Status Radio ───────────────────
  // ASP: Approve / Disapprove / Cancel / Close
  const [status,          setStatus]          = useState(""); // "Approved"|"Disapproved"|"Canceled"|"Closed"

  // ─────────────────── Control disable flags ───────────────────
  // ASP: Cancel → closeEnabled=false ; Close → cancel/approve/disapprove disabled
  // ASP: isOutwarded → all disabled
  const [closeEnabled,    setCloseEnabled]    = useState(true);
  const [cancelEnabled,   setCancelEnabled]   = useState(true);
  const [approveEnabled,  setApproveEnabled]  = useState(true);
  const [disapproveEnabled, setDisapproveEnabled] = useState(true);
  const [remarksEnabled,  setRemarksEnabled]  = useState(true);
  const [submitVisible,   setSubmitVisible]   = useState(true);

  // ─────────────────── Grid State ───────────────────
  const [items,     setItems]     = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ─────────────────── Pagination ───────────────────
  const [page,      setPage]      = useState(1);
  const PAGE_SIZE   = 10;
  const totalPages  = Math.ceil(items.length / PAGE_SIZE);
  const pagedRows   = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ═══════════════════════════════════════════════════════
  // Status radio change handler
  // ASP: Cancel → Close disabled ; Close → Cancel/Approve/Disapprove disabled
  // ═══════════════════════════════════════════════════════
  const handleStatusChange = (val) => {
    setStatus(val);
    // Reset all first
    setCloseEnabled(true);
    setCancelEnabled(true);
    setApproveEnabled(true);
    setDisapproveEnabled(true);

    if (val === "Canceled") {
      setCloseEnabled(false);    // ASP: rbtn_Close.Enabled = false
    }
    if (val === "Closed") {
      setCancelEnabled(false);   // ASP: rbtn_Cancel.Enabled = false
      setApproveEnabled(false);  // ASP: rbtn_Approve.Enabled = false
      setDisapproveEnabled(false); // ASP: rbtn_Disapprove.Enabled = false
    }
  };

  // ═══════════════════════════════════════════════════════
  // FillGrid — ASP: FillGrid()
  // SP: usp_GetSTNRequestForApproval STNRequisitionId
  //   → Tables[0]: header row
  //   → Tables[1]: item rows
  // ═══════════════════════════════════════════════════════
  const loadData = useCallback(async () => {
    if (!stnRequisitionId) {
      toast.error("STN Requisition ID not found. Please go back and select a record.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-approval/detail`, {
        params: { stnRequisitionId },
      });

      if (res.data?.success) {
        const header = res.data.header || {};
        const rows   = res.data.items  || [];

        setStnNo(header.fld_STNRequestNo || "");
        setDate(fmtDate(header.fld_Date));
        setRequestedStore(header.fld_Storename      || "");
        setDepartment(header.fld_departmentname     || "");
        setEmployee(header.fld_Employee             || "");
        setIndentRef(header.fld_Indentref           || "");
        setRemarks(header.fld_Remarks               || "");
        setItems(rows);
        setPage(1);

        // ── Outward check ──
        // ASP: usp_IsSTNRequestOutwarded → if "True" disable all + hide submit
        if (res.data.isOutwarded === true) {
          setApproveEnabled(false);
          setCancelEnabled(false);
          setCloseEnabled(false);
          setDisapproveEnabled(false);
          setRemarksEnabled(false);
          setSubmitVisible(false);
        }

        // ── Pre-select status from session ──
        // ASP: Session["status"] → Approved / DisApproved / Cancelled
        if (!res.data.isOutwarded) {
          if (sessionStatus === "Approved") {
            setStatus("Approved");
          } else if (sessionStatus === "DisApproved") {
            setStatus("Disapproved");
          } else if (sessionStatus === "Cancelled") {
            setStatus("Canceled");
            setCloseEnabled(false); // ASP: rbtn_Close.Enabled = false
          }
        }
      } else {
        toast.error(res.data?.message || "Failed to load data");
      }
    } catch (e) {
      toast.error("Load failed: " + (e?.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  }, [stnRequisitionId, sessionStatus]);

  useEffect(() => { loadData(); }, [loadData]);

  // ═══════════════════════════════════════════════════════
  // Submit — ASP: ibtn_submit_Click
  // SP: usp_UpdateSTNRequestStatus STNRequisitionId, operation, remarks
  // On success → open SMS window + navigate back to listing
  // On failure → show error alert
  // ═══════════════════════════════════════════════════════
  const handleSubmit = async () => {
    if (!status) {
      toast.warning("Please select a status (Approve / Disapprove / Cancel / Close)");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${BASE_URL}/api/inventory/stn-approval/update-status`, {
        stnRequisitionId,
        operation: status,
        remarks:   remarks.trim(),
      });

      if (res.data?.success) {
        // ASP: open SMS window
        const dateNow      = new Date().toLocaleDateString("en-GB"); // dd/MM/yyyy
        const billnoanddate = `Stn Req No: ${stnNo}-${status},${dateNow}`;
        const smsPage       = `/SMS/SendSMS?No=${encodeURIComponent(billnoanddate)}&Page=STN2`;
        window.open(
          smsPage,
          "_blank",
          "width=400,height=150,scrollbars=yes,menubar=no,resizable=no,directories=no,location=no"
        );

        toast.success(`${status} successfully`);
        setTimeout(() => navigate("/Inventory/STNRequisition_Listing"), 1200);
      } else {
        toast.error(res.data?.message || "Please select proper operation and Retry");
      }
    } catch (e) {
      toast.error("Please select proper operation and Retry");
    } finally {
      setSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────────────
  // Radio option config
  // ─────────────────────────────────────────────────────
  const radioOptions = [
    {
      value:   "Approved",
      label:   "Approve",
      enabled: approveEnabled,
      icon:    <CheckCircle size={14} />,
      color:   "text-green-500",
      ring:    "ring-green-400",
    },
    {
      value:   "Disapproved",
      label:   "Disapprove",
      enabled: disapproveEnabled,
      icon:    <XCircle size={14} />,
      color:   "text-red-500",
      ring:    "ring-red-400",
    },
    {
      value:   "Canceled",
      label:   "Cancel",
      enabled: cancelEnabled,
      icon:    <MinusCircle size={14} />,
      color:   "text-orange-500",
      ring:    "ring-orange-400",
    },
    {
      value:   "Closed",
      label:   "Close",
      enabled: closeEnabled,
      icon:    <Lock size={14} />,
      color:   "text-gray-500",
      ring:    "ring-gray-400",
    },
  ];

  // ─────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────
  return (
    <LayoutWrapper>
      <HeaderBar title="STN Requisition Approval" />
      <ToastContainer position="top-right" autoClose={3000} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen px-4 py-6 ${isDark ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900"}`}>

        {/* ══════════════════════════════════════════════
            APPROVAL FORM CARD
        ══════════════════════════════════════════════ */}
        <div className={`${cardBase} mb-6`}>

          {/* Card Header */}
          <div className={`px-5 py-3 flex items-center justify-between ${primaryBtnClass}`}>
            <h2 className="text-sm font-bold text-white tracking-wider uppercase">
              STN Requisition Approval
            </h2>
            <div className="flex items-center gap-3">
              <span className="text-white/70 text-xs font-medium">INFRA SCREEN : A-26</span>
              {/* ASP: Back button → stnrequestionlisting.aspx */}
              <button
                onClick={() => navigate("/Inventory/STNRequisition_Listing")}
                className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition"
              >
                <ArrowLeft size={14} /> Back
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCcw size={22} className="animate-spin text-blue-500 mr-3" />
              <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>Loading...</span>
            </div>
          ) : (
            <div className="p-5 space-y-5">

              {/* ── Row 1: STN Number + Date ── */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelBase}>STN Number</label>
                  {/* ASP: lbl_STNno — purple bold */}
                  <div className={`mt-1 w-full px-3 py-2 rounded-xl border text-sm font-bold
                    ${isDark
                      ? "bg-gray-700/50 border-white/10 text-purple-300"
                      : "bg-gray-50 border-gray-200 text-purple-700"}`}>
                    {stnNo || "—"}
                  </div>
                </div>
                <div>
                  <label className={labelBase}>Date</label>
                  <div className={`mt-1 ${readonlyField}`}>{date || "—"}</div>
                </div>
              </div>

              {/* ── Row 2: Requested Store + Employee ── */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelBase}>Requested Store</label>
                  <div className={`mt-1 ${readonlyField}`}>{requestedStore || "—"}</div>
                </div>
                <div>
                  <label className={labelBase}>Employee</label>
                  <div className={`mt-1 ${readonlyField}`}>{employee || "—"}</div>
                </div>
              </div>

              {/* ── Row 3: Department + Indent Ref ── */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelBase}>Department</label>
                  <div className={`mt-1 ${readonlyField}`}>{department || "—"}</div>
                </div>
                <div>
                  <label className={labelBase}>Indent Ref</label>
                  <div className={`mt-1 ${readonlyField}`}>{indentRef || "—"}</div>
                </div>
              </div>

              {/* ── Row 4: Remarks + Status ── */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {/* Remarks — ASP: txt_remarks (editable TextArea) */}
                <div>
                  <label className={labelBase}>Remarks</label>
                  <textarea
                    value={remarks}
                    onChange={e => setRemarks(e.target.value)}
                    disabled={!remarksEnabled}
                    rows={4}
                    className={`mt-1 ${inputBase} resize-none disabled:opacity-50 disabled:cursor-not-allowed`}
                    placeholder="Enter remarks..."
                  />
                </div>

                {/* Status radio buttons */}
                {/* ASP: rbtn_Approve / rbtn_Disapprove / rbtn_Cancel / rbtn_Close */}
                <div>
                  <label className={labelBase}>Status</label>
                  <div className={`mt-1 rounded-xl border p-4 space-y-3
                    ${isDark ? "border-white/10 bg-gray-800/40" : "border-gray-200 bg-gray-50"}`}>
                    {radioOptions.map(opt => (
                      <label
                        key={opt.value}
                        className={`flex items-center gap-3 cursor-pointer group
                          ${!opt.enabled ? "opacity-40 cursor-not-allowed pointer-events-none" : ""}`}
                      >
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition
                          ${status === opt.value
                            ? `border-transparent ${opt.ring.replace("ring-", "bg-").replace("-400", "-500")}`
                            : (isDark ? "border-gray-500" : "border-gray-300")}
                          ${opt.enabled ? `group-hover:${opt.ring}` : ""}`}
                          onClick={() => opt.enabled && handleStatusChange(opt.value)}
                        >
                          {status === opt.value && (
                            <div className="w-2 h-2 rounded-full bg-white" />
                          )}
                        </div>
                        <span
                          onClick={() => opt.enabled && handleStatusChange(opt.value)}
                          className={`flex items-center gap-1.5 text-sm font-medium transition
                            ${status === opt.value ? opt.color : (isDark ? "text-gray-300" : "text-gray-700")}`}
                        >
                          <span className={status === opt.value ? opt.color : (isDark ? "text-gray-500" : "text-gray-400")}>
                            {opt.icon}
                          </span>
                          {opt.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Submit Button ── */}
              {/* ASP: ibtn_submit — hidden if isOutwarded */}
              {submitVisible && (
                <div className="flex justify-center pt-2">
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className={`flex items-center gap-2 ${primaryBtnClass} ${primaryHoverClass}
                      text-white px-8 py-2.5 rounded-xl text-sm font-semibold shadow transition
                      disabled:opacity-60 disabled:cursor-not-allowed`}
                  >
                    {submitting
                      ? <RefreshCcw size={15} className="animate-spin" />
                      : <Send size={15} />}
                    {submitting ? "Submitting..." : "Submit"}
                  </button>
                </div>
              )}

            </div>
          )}
        </div>

        {/* ══════════════════════════════════════════════
            ITEMS GRID CARD
            ASP: gv_listitems — Tables[1] from SP
        ══════════════════════════════════════════════ */}
        <div className={cardBase}>

          {/* Grid Card Header */}
          <div className={`px-5 py-3 ${primaryBtnClass}`}>
            <h3 className="text-sm font-bold text-white tracking-wider uppercase">
              Item Details
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={`${thBase} text-center`}>Sl No</th>
                  <th className={thBase}>Item Code</th>
                  <th className={thBase}>Item Name</th>
                  <th className={thBase}>Part No</th>
                  <th className={`${thBase} text-center`}>UOM</th>
                  <th className={`${thBase} text-center`}>Qty</th>
                  <th className={`${thBase} text-center`}>Due Date</th>
                  <th className={thBase}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className={`${tdBase} text-center py-12`}>
                      <RefreshCcw size={20} className="animate-spin inline mr-2 text-blue-500" />
                      Loading...
                    </td>
                  </tr>
                ) : pagedRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className={`${tdBase} text-center py-12 text-gray-400`}>
                      No Records Found
                    </td>
                  </tr>
                ) : pagedRows.map((row, idx) => {
                  const globalIdx = (page - 1) * PAGE_SIZE + idx;
                  return (
                    <tr
                      key={globalIdx}
                      className={`border-t transition
                        ${isDark ? "border-white/5" : "border-gray-100"}
                        ${globalIdx % 2 === 0
                          ? (isDark ? "bg-gray-800/20 hover:bg-white/5" : "bg-gray-50/60 hover:bg-blue-50/40")
                          : (isDark ? "hover:bg-white/5" : "hover:bg-blue-50/40")}`}
                    >
                      <td className={`${tdBase} text-center`}>{globalIdx + 1}</td>
                      <td className={tdBase}>{row.fld_itemcode   || "—"}</td>
                      <td className={tdBase}>{row.fld_itemname   || "—"}</td>
                      <td className={tdBase}>{row.fld_itemPartNo || "—"}</td>
                      <td className={`${tdBase} text-center`}>{row.fld_uomname  || "—"}</td>
                      <td className={`${tdBase} text-center`}>{row.fld_Qty      ?? "—"}</td>
                      <td className={`${tdBase} text-center`}>{fmtDate(row.fld_DueDate)}</td>
                      <td className={tdBase}>{row.fld_Remarks    || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination + record count */}
          {items.length > 0 && (
            <div className={`px-4 py-3 border-t text-xs flex items-center justify-between flex-wrap gap-2
              ${isDark ? "border-white/10 text-gray-400" : "border-gray-100 text-gray-500"}`}>
              <span>
                Showing {Math.min((page - 1) * PAGE_SIZE + 1, items.length)}–{Math.min(page * PAGE_SIZE, items.length)} of {items.length}
              </span>
              <Pagination page={page} totalPages={totalPages} onPage={p => setPage(p)} />
            </div>
          )}
        </div>

      </div>
    </LayoutWrapper>
  );
}